import * as THREE from 'three';
import { App } from 'antd';
import { useAppStore, Layer, RenderSettings } from '../store/useAppStore';
import { TEX_RESOLUTIONS } from '../paintingConstants';
import { readRTToDataURL, loadDataURLToRT } from '../utils/webglTextureUtils';
import { pushRecentProject, storeRecentHandle, storeLastHandle, getLastHandle } from '../components/ProjectModal';

type LayerRTMap = Map<string, { a: THREE.WebGLRenderTarget; b: THREE.WebGLRenderTarget; active: 'a' | 'b' }>;

export interface ProjectIORefs {
  glRef: React.RefObject<any>;
  readRTPixelBufferRef: React.MutableRefObject<Uint8Array | null>;
  copyMaterial: React.RefObject<THREE.ShaderMaterial | null>;
  copyScene: React.RefObject<THREE.Scene | null>;
  orthoCamera: React.RefObject<THREE.OrthographicCamera | null>;
  layerRTs: React.MutableRefObject<LayerRTMap>;
  /** Call to wipe undo/redo history when loading or creating a project. */
  clearHistoryRef: React.MutableRefObject<() => void>;
  savedFileHandleRef: React.MutableRefObject<any>;
  isDirtyRef: React.MutableRefObject<boolean>;
  ensureLayerRTsRef: React.MutableRefObject<(layers: Layer[]) => void>;
  flattenLayersRef: React.MutableRefObject<() => void>;
  resetHeroStateRef: React.MutableRefObject<() => void>;
  generateAllLayerThumbnailsRef: React.MutableRefObject<() => void>;
  scheduleBumpRef: React.MutableRefObject<(after?: () => void) => void>;
  /** Pre-encoded WebP data URLs keyed by layer id; populated by the background encoder in Viewport3D. */
  layerTextureCacheRef: React.MutableRefObject<Map<string, string>>;
}

export interface ProjectIOActions {
  buildProjectBlob: (name: string) => Blob;
  /** Same as buildProjectBlob but returns the raw JSON string — synchronous, safe to call from requestIdleCallback. */
  buildProjectJson: (name: string) => string;
  saveProject: (name: string) => Promise<void>;
  loadProject: (file: File, handle?: any) => Promise<void>;
  newProject: () => void;
  saveCurrentProject: () => Promise<void>;
}

/**
 * Provides project save/load/new operations.
 * All functions only access refs and getState() — safe to call at any time.
 */
export function useProjectIO(refs: ProjectIORefs): ProjectIOActions {
  const { notification } = App.useApp();
  const {
    glRef,
    readRTPixelBufferRef,
    copyMaterial,
    copyScene,
    orthoCamera,
    layerRTs,
    clearHistoryRef,
    savedFileHandleRef,
    isDirtyRef,
    ensureLayerRTsRef,
    flattenLayersRef,
    resetHeroStateRef,
    generateAllLayerThumbnailsRef,
    scheduleBumpRef,
    layerTextureCacheRef,
  } = refs;

  const buildProjectBlob = (name: string): Blob => {
    return new Blob([buildProjectJson(name)], { type: 'application/json' });
  };

  const buildProjectJson = (name: string): string => {
    const store = useAppStore.getState();
    const layerTextures: Record<string, string> = {};
    const gl = glRef.current;
    const cache = layerTextureCacheRef.current;
    if (gl) {
      for (const layer of store.layers) {
        const cached = cache.get(layer.id);
        if (cached) {
          layerTextures[layer.id] = cached;
        } else {
          const lRTData = layerRTs.current.get(layer.id);
          if (!lRTData) continue;
          const activeRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
          layerTextures[layer.id] = readRTToDataURL(gl, activeRT, readRTPixelBufferRef);
        }
      }
    }
    const project = {
      version: '1',
      name,
      savedAt: new Date().toISOString(),
      layers: store.layers,
      selectedLayerId: store.selectedLayerId,
      layerKeyframes: store.layerKeyframes,
      fps: store.fps,
      totalFrames: store.totalFrames,
      renderSettings: store.renderSettings,
      texResLabel: store.texRes.label,
      layerTextures,
    };
    return JSON.stringify(project);
  };

  const saveProject = async (name: string): Promise<void> => {
    const blob = buildProjectBlob(name);
    const safeName = name.replace(/[/\\:*?"<>|]/g, '_');
    if (typeof (window as any).showSaveFilePicker === 'function') {
      try {
        const lastHandle = await getLastHandle();
        const fileHandle = await (window as any).showSaveFilePicker({
          suggestedName: `${safeName}.balls`,
          types: [{ description: 'StoryBall Project', accept: { 'application/json': ['.balls'] } }],
          startIn: lastHandle ?? undefined,
        });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        savedFileHandleRef.current = fileHandle;
        storeRecentHandle(name, fileHandle);
        storeLastHandle(fileHandle);
        isDirtyRef.current = false;
        notification.success({ title: 'Project saved', description: `"${name}" saved successfully.`, placement: 'bottomRight', duration: 3 });
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        throw err;
      }
    } else {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${safeName}.balls`;
      anchor.click();
      URL.revokeObjectURL(url);
      savedFileHandleRef.current = null;
      isDirtyRef.current = false;
      notification.success({ title: 'Project saved', description: `"${name}" downloaded as "${safeName}.balls".`, placement: 'bottomRight', duration: 3 });
    }
  };

  const loadProject = async (file: File, handle?: any): Promise<void> => {
    const text = await file.text();
    const data = JSON.parse(text) as {
      version: string;
      name?: string;
      savedAt?: string;
      layers: Layer[];
      selectedLayerId: string | null;
      layerKeyframes: Record<string, any>;
      fps: number;
      totalFrames: number;
      renderSettings?: RenderSettings;
      texResLabel?: string;
      layerTextures: Record<string, string>;
    };

    layerRTs.current.forEach(({ a, b }) => { a.dispose(); b.dispose(); });
    layerRTs.current.clear();
    clearHistoryRef.current();

    const store = useAppStore.getState();
    store.restoreProject({
      layers: data.layers,
      selectedLayerId: data.selectedLayerId,
      layerKeyframes: data.layerKeyframes,
      fps: data.fps,
      totalFrames: data.totalFrames,
      renderSettings: data.renderSettings,
    });
    
    // Restore texture resolution if saved in the project file
    if (data.texResLabel) {
      const savedRes = TEX_RESOLUTIONS.find((r) => r.label === data.texResLabel);
      if (savedRes) {
        store.setTexRes(savedRes);
      }
    }
    
    resetHeroStateRef.current();

    ensureLayerRTsRef.current(data.layers);

    const gl = glRef.current;
    const cm = copyMaterial.current;
    const cs = copyScene.current;
    const oc = orthoCamera.current;
    if (gl && cm && cs && oc) {
      await Promise.all(
        data.layers.map(async (layer) => {
          const textureData = data.layerTextures[layer.id];
          if (!textureData) return;
          const lRTData = layerRTs.current.get(layer.id);
          if (!lRTData) return;
          await loadDataURLToRT(textureData, lRTData.active === 'a' ? lRTData.a : lRTData.b, gl, cm, cs, oc);
        })
      );
    }

    flattenLayersRef.current();

    // Regenerate layer thumbnails and bump the UV panel so they appear immediately.
    generateAllLayerThumbnailsRef.current();
    scheduleBumpRef.current();

    const projectName = data.name ?? file.name.replace(/\.balls$/, '');
    pushRecentProject({ name: projectName, savedAt: data.savedAt ?? new Date().toISOString() });
    useAppStore.getState().setCurrentProjectName(projectName);
    savedFileHandleRef.current = handle ?? null;
    if (handle) storeRecentHandle(projectName, handle);
    isDirtyRef.current = false;
    notification.success({ title: 'Project loaded', description: `"${projectName}" opened successfully.`, placement: 'bottomRight', duration: 3 });
  };

  const newProject = () => {
    layerRTs.current.forEach(({ a, b }) => { a.dispose(); b.dispose(); });
    layerRTs.current.clear();
    clearHistoryRef.current();
    useAppStore.getState().resetToDefaults();
    useAppStore.getState().setCurrentProjectName('Untitled');
    savedFileHandleRef.current = null;
    isDirtyRef.current = false;
  };

  const saveCurrentProject = async (): Promise<void> => {
    if (savedFileHandleRef.current) {
      try {
        const { currentProjectName } = useAppStore.getState();
        const blob = buildProjectBlob(currentProjectName);
        const writable = await savedFileHandleRef.current.createWritable();
        await writable.write(blob);
        await writable.close();
        notification.success({ title: 'Project saved', description: `"${currentProjectName}" saved.`, placement: 'bottomRight', duration: 3 });
      } catch (err: any) {
        if (err?.name !== 'AbortError') throw err;
      }
    } else {
      useAppStore.getState().setProjectModalOpen(true);
    }
  };

  return { buildProjectBlob, buildProjectJson, saveProject, loadProject, newProject, saveCurrentProject };
}
